import { ElementStore, Settings } from 'component/Carousel/store/ElementStore'
import { makeAutoObservable, reaction } from 'mobx'
import { getNextKey } from 'util/id'

export class CarouselStore<T = unknown> {
  readonly key = getNextKey()

  readonly items: T[]
  private _index = 0

  private readonly settings: Settings
  private element?: ElementStore

  constructor(items: T[], settings?: Settings) {
    makeAutoObservable(this)
    this.items = items
    this.settings = settings ?? {}
  }

  get stable(): boolean {
    return !!this.element?.stable
  }

  get many(): boolean {
    return this.items.length > 1
  }

  get index(): number {
    return this._index
  }

  set index(index: number) {
    this.setIndex(index, true)
  }

  get item(): T | undefined {
    return this.items[this._index]
  }

  set item(item: T | undefined) {
    const index = this.items.indexOf(item!)
    this.index = index < 0 ? 0 : index
  }

  get root(): HTMLElement | undefined {
    return this.element?.root
  }

  set root(root: HTMLElement | null | undefined) {
    if (root) this.start(root)
    else this.stop()
  }

  deleteItem(item: T) {
    const index = this.items.indexOf(item)
    if (index < 0) return

    this.items.splice(index, 1)
    if (this.element) this.element.setCount(this.items.length)

    this.setIndex(index)
  }

  private setIndex(index: number, smooth?: boolean) {
    if (this.element) this.element.setIndex(index, smooth)
    else this._index = index
  }

  private start(root: HTMLElement) {
    const element = new ElementStore(root, this.items.length, this.settings)
    this.element = element
    element.setIndex(this.index)
    reaction(() => element.index, index => this._index = index)
  }

  private stop() {
    if (this.element) this.element.destroy()
    this.element = undefined
  }
}
